import { useEffect, useRef, useState } from 'react';
import { addSeconds, differenceInSeconds } from 'date-fns';
import { useNavigate } from 'react-router';
// Worker-based interval so the countdown keeps ticking in a throttled
// background tab; the timeout below stays native, a delayed cooldown is fine.
import { clearInterval, setInterval } from 'worker-timers';

import { getTwoFactorVerificationFailure } from '../api/logic/getTwoFactorVerificationFailure';
import {
  useRequestNewTwoFactorCode,
  useVerifyCode,
} from '../api/middle-office/authentication';
import {
  TwoFactorVerificationFailure,
  TwoFactorVerificationFailureKey,
} from '../api/types/TwoFactorVerificationFailure';
import { useEventHandler } from './useEventHandler';

interface VerifyCodeResponse {
  success: boolean;
  data?: any;
}

// Mirrors the backend code lifetime. The initial code is sent by the login
// backend, so the mount countdown starts from this client constant; a resend
// re-syncs it precisely from the send response's expiresIn.
const CODE_LIFETIME_SECONDS = 120;

const noFailure: TwoFactorVerificationFailure = { message: '' };

const failuresLeavingNoLiveCode: readonly TwoFactorVerificationFailureKey[] = [
  'two_factor.code_expired',
  'two_factor.attempts_exhausted',
];

const failuresSpeakingForThemselves: readonly TwoFactorVerificationFailureKey[] =
  [...failuresLeavingNoLiveCode, 'two_factor.request_throttled'];

export const useTwoFactorAuth = (
  twoFactorMethod: string | undefined,
  loginPath: string,
) => {
  const [authCode, setAuthCode] = useState<string>('');
  const [failure, setFailure] =
    useState<TwoFactorVerificationFailure>(noFailure);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [codeRequested, setCodeRequested] = useState<boolean>(false);
  const [resendCodeTimer, setResendCodeTimer] = useState<number>(
    CODE_LIFETIME_SECONDS,
  );
  const codeRequestSupported = twoFactorMethod !== 'app';
  const navigate = useNavigate();
  const intervalRef = useRef<number | undefined>(undefined);
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);

  const { verifyCode: verifyCodeMutation } = useVerifyCode();
  const { requestNewTwoFactorCode } = useRequestNewTwoFactorCode();

  // worker-timers throws on ids it no longer tracks, so every clear goes
  // through this guard and the ref is emptied alongside the interval
  const clearCodeExpiryInterval = useEventHandler(() => {
    if (intervalRef.current !== undefined) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  });

  const startCodeExpiryCountdown = useEventHandler(
    (secondsToExpiration: number) => {
      const expiresAt = addSeconds(new Date(), secondsToExpiration);

      clearCodeExpiryInterval();

      setResendCodeTimer(secondsToExpiration);

      intervalRef.current = setInterval(() => {
        const diffInSeconds = differenceInSeconds(expiresAt, new Date());

        setResendCodeTimer(diffInSeconds);

        if (diffInSeconds < 1) clearCodeExpiryInterval();
      }, 1000);
    },
  );

  const stopCodeExpiryCountdown = useEventHandler(() => {
    clearCodeExpiryInterval();
    setResendCodeTimer(0);
  });

  const verifyCode = useEventHandler(async (): Promise<VerifyCodeResponse> => {
    setIsLoading(true);
    try {
      const res = await verifyCodeMutation({ code: authCode });
      return { success: true, data: res };
    } catch (e: any) {
      const verificationFailure = getTwoFactorVerificationFailure(e);

      setFailure(verificationFailure);

      if (
        verificationFailure.key &&
        failuresLeavingNoLiveCode.includes(verificationFailure.key)
      ) {
        stopCodeExpiryCountdown();
      }

      return { success: false };
    } finally {
      setIsLoading(false);
    }
  });

  const requestNewCode = useEventHandler(async () => {
    if (!codeRequestSupported) return;

    try {
      const res = await requestNewTwoFactorCode();
      const secondsToExpiration = res?.data.expiresIn ?? CODE_LIFETIME_SECONDS;

      setFailure(noFailure);
      startCodeExpiryCountdown(secondsToExpiration);
    } catch (e: any) {
      if (e.response?.status === 500) {
        navigate(loginPath);
        return;
      }

      // A running countdown means the code already in the inbox is still live,
      // so a throttled resend must not tell the user it expired.
      const throttledOverLiveCode =
        e.response?.status === 429 && resendCodeTimer > 0;
      if (!throttledOverLiveCode) {
        stopCodeExpiryCountdown();
      }
      setFailure(getTwoFactorVerificationFailure(e));
    }

    setCodeRequested(true);
    timeoutRef.current = setTimeout(() => setCodeRequested(false), 5000);
  });

  // The login backend sends the initial code, so mount makes no request. For
  // the mail/SMS method it starts the countdown from the client TTL constant
  // that mirrors the backend code lifetime (a resend re-syncs it precisely);
  // the app method has no emailed code, so it starts no countdown. Gated on a
  // known method so an app user is not misread while their user loads.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current || twoFactorMethod === undefined) return;
    startedRef.current = true;

    if (codeRequestSupported) {
      startCodeExpiryCountdown(CODE_LIFETIME_SECONDS);
    }
  }, [twoFactorMethod, codeRequestSupported, startCodeExpiryCountdown]);

  useEffect(
    () => () => {
      clearCodeExpiryInterval();
      clearTimeout(timeoutRef.current);
    },
    [clearCodeExpiryInterval],
  );

  const errorExplainsCodeExpiry = failure.key
    ? failuresSpeakingForThemselves.includes(failure.key)
    : Boolean(failure.message);

  return {
    authCode,
    setAuthCode,
    error: failure.message,
    errorExplainsCodeExpiry,
    isLoading,
    codeRequested,
    verifyCode,
    requestNewCode,
    codeRequestSupported,
    resendCodeTimer,
  };
};
